import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveStore } from "@/hooks/use-active-store";
import { useCreateCoupon, useUpdateCoupon } from "@/hooks/use-orders";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  coupon?: any;
};

const defaultForm = {
  code: "",
  discount_type: "percent",
  amount: "",
  description: "",
  individual_use: false,
  exclude_sale_items: false,
  minimum_amount: "",
  maximum_amount: "",
  usage_limit: "",
  usage_limit_per_user: "",
  free_shipping: false,
};

function parseDate(str: string | null | undefined): Date | undefined {
  if (!str) return undefined;
  const d = new Date(str + "T00:00:00");
  return isNaN(d.getTime()) ? undefined : d;
}

function formatDateStr(date: Date | undefined): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function CouponDialog({ open, onOpenChange, coupon }: Props) {
  const isEdit = !!coupon;
  const { storeId } = useActiveStore();
  const createMut = useCreateCoupon(storeId);
  const updateMut = useUpdateCoupon(storeId);

  const [form, setForm] = useState(defaultForm);
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    if (coupon) {
      setForm({
        code: coupon.code || "",
        discount_type: coupon.discount_type || "percent",
        amount: coupon.amount || "",
        description: coupon.description || "",
        individual_use: coupon.individual_use || false,
        exclude_sale_items: coupon.exclude_sale || false,
        minimum_amount: coupon.minimum_amount || "",
        maximum_amount: coupon.maximum_amount || "",
        usage_limit: coupon.usage_limit ? String(coupon.usage_limit) : "",
        usage_limit_per_user: coupon.usage_limit_per_user ? String(coupon.usage_limit_per_user) : "",
        free_shipping: coupon.free_shipping || false,
      });
      setExpiryDate(parseDate(coupon.expiry_date));
    } else {
      setForm(defaultForm);
      setExpiryDate(undefined);
    }
  }, [coupon, open]);

  const set = (key: string, value: any) => setForm((f) => ({ ...f, [key]: value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error("Kod kupona je obavezan");
      return;
    }
    if (!form.amount) {
      toast.error("Iznos je obavezan");
      return;
    }

    const payload: any = {
      code: form.code.trim().toLowerCase(),
      discount_type: form.discount_type,
      amount: form.amount,
      description: form.description,
      individual_use: form.individual_use,
      exclude_sale_items: form.exclude_sale_items,
      free_shipping: form.free_shipping,
    };

    if (expiryDate) payload.date_expires = formatDateStr(expiryDate);
    if (form.minimum_amount) payload.minimum_amount = form.minimum_amount;
    if (form.maximum_amount) payload.maximum_amount = form.maximum_amount;
    if (form.usage_limit) payload.usage_limit = Number(form.usage_limit);
    if (form.usage_limit_per_user) payload.usage_limit_per_user = Number(form.usage_limit_per_user);

    try {
      if (isEdit) {
        await updateMut.mutateAsync({ couponId: coupon.id, ...payload });
        toast.success("Kupon ažuriran");
      } else {
        await createMut.mutateAsync(payload);
        toast.success("Kupon kreiran");
      }
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Greška pri spremanju kupona");
    }
  }

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">{isEdit ? "Uredi kupon" : "Novi kupon"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Kod *</Label>
              <Input
                className="h-8 text-xs font-mono"
                value={form.code}
                onChange={(e) => set("code", e.target.value)}
                placeholder="POPUST20"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Tip popusta</Label>
              <Select value={form.discount_type} onValueChange={(v) => set("discount_type", v)}>
                <SelectTrigger className="h-8 text-xs w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Postotak (%)</SelectItem>
                  <SelectItem value="fixed_cart">Fiksni (košarica)</SelectItem>
                  <SelectItem value="fixed_product">Fiksni (proizvod)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Iznos *</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                placeholder={form.discount_type === "percent" ? "20" : "10.00"}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Datum isteka</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "h-8 w-full justify-start text-xs font-normal",
                      !expiryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                    {expiryDate
                      ? expiryDate.toLocaleDateString("bs-BA", { day: "2-digit", month: "2-digit", year: "numeric" })
                      : "Odaberi datum"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={expiryDate}
                    onSelect={(date) => setExpiryDate(date)}
                    disabled={{ before: new Date() }}
                  />
                  {expiryDate && (
                    <div className="px-3 pb-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => setExpiryDate(undefined)}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Ukloni datum
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-xs">Opis</Label>
            <Input
              className="h-8 text-xs"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Interni opis kupona..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Min. iznos narudžbe</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                step="0.01"
                min="0"
                value={form.minimum_amount}
                onChange={(e) => set("minimum_amount", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Max. iznos narudžbe</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                step="0.01"
                min="0"
                value={form.maximum_amount}
                onChange={(e) => set("maximum_amount", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Ukupni limit korištenja</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min="0"
                value={form.usage_limit}
                onChange={(e) => set("usage_limit", e.target.value)}
                placeholder="Neograničeno"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Limit po korisniku</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min="0"
                value={form.usage_limit_per_user}
                onChange={(e) => set("usage_limit_per_user", e.target.value)}
                placeholder="Neograničeno"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <label className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={form.individual_use}
                onCheckedChange={(v) => set("individual_use", !!v)}
              />
              Samo individualna upotreba
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={form.exclude_sale_items}
                onCheckedChange={(v) => set("exclude_sale_items", !!v)}
              />
              Isključi artikle na sniženju
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={form.free_shipping}
                onCheckedChange={(v) => set("free_shipping", !!v)}
              />
              Besplatna dostava
            </label>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Odustani
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              {isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {isEdit ? "Spremi" : "Kreiraj"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
